import { Flex, Box, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import CopyClipboard from 'components/CopyClipboard';
import Loader from 'components/Loader';
import { AnimatedMascot } from 'components/Mascot';
import { useExchangeRUNByADPAuthCode } from 'hooks/integrations/RUNByADP/useExchangeRUNByADPAuthCode';
import { useFetchRUNByADPConnectorCode } from 'hooks/integrations/RUNByADP/useFetchRUNByADPConnectorCode';
import { useFetchRUNByADPConnectUrl } from 'hooks/integrations/RUNByADP/useFetchRUNByADPConnectUrl';
import { useSyncRUNByADPAllModules } from 'hooks/integrations/RUNByADP/useSyncRUNByADPAllModules';

const ConnectorSetup = () => {
  const { t } = useTranslation(['integrations']);

  const [externalPopup, setExternalPopup] = useState<Window | null>(null);

  const { status: connectorCodeStatus, data: connectorCodeData } = useFetchRUNByADPConnectorCode();
  const { mutateAsync: mutateAsyncSyncAllModules, status: syncAllModulesStatus } = useSyncRUNByADPAllModules();
  const { status: connectURLStatus, data: connectURLData } = useFetchRUNByADPConnectUrl();
  const { mutate, status: exchangeAuthCodeStatus } = useExchangeRUNByADPAuthCode();

  useEffect(() => {
    if (!externalPopup) {
      return;
    }

    const timer = setInterval(() => {
      if (!externalPopup) {
        if (timer) clearInterval(timer);
        return;
      }

      if (externalPopup?.closed) {
        if (timer) clearInterval(timer);
        mutateAsyncSyncAllModules();
        return;
      }

      try {
        const currentUrl = externalPopup.location.href;
        if (!currentUrl) {
          return;
        }
        const searchParams = new URL(currentUrl).searchParams;
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (code && state) {
          externalPopup.close();
          console.log(`The popup URL has URL param of code=${code} and state=${state}`);

          const postData = {
            code,
            state,
          };
          mutate(postData);
          mutateAsyncSyncAllModules();

          setExternalPopup(null);
          if (timer) clearInterval(timer);
        }
      } catch (err) {}
    }, 1000);
  }, [externalPopup, mutate]);

  const onEnable = () => {
    const width = 600;
    const height = 420;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    const title = `ADP Login`;
    const url = connectURLData?.payload?.consent_url;

    const popup = window.open(url, title, `width=${width},height=${height},left=${left},top=${top}`);

    setExternalPopup(popup);
  };

  if (syncAllModulesStatus === 'loading' || exchangeAuthCodeStatus === 'loading') return <Loader height="200px" />;

  return (
    <div>
      <Flex flexDirection="column" alignItems="flex-end">
        <Flex justifyContent="flex-end" mb="3">
          <CopyClipboard
            copyValue={connectorCodeData?.payload.code}
            isLoading={connectorCodeStatus === 'loading'}
            toolTipDesc={t('integrations:RUNByADP.copyThisKeyToUserTheADPConnector')}
            label={t('integrations:RUNByADP.copyThisKeyToUserTheADPConnector')}
          />
        </Flex>
        <Box
          padding="20px 40px"
          border="1px solid"
          borderColor="gray.400"
          background="white"
          textAlign="center"
          w="full"
          mb={6}
        >
          <AnimatedMascot type="barking" />
          <Text>{t('integrations:clickToEnable')}.</Text>
        </Box>
        <Flex alignItems="center" wrap="wrap" justifyContent="flex-end">
          <a href={import.meta.env.VITE_RUN_ADP_URL} target="_blank" rel="noreferrer">
            <Button variant="primary-ghost" mb="4">
              {t('integrations:RUNByADP.getTheTimeeroDataConnectorForADP')}
            </Button>
          </a>
          <Button ml="4" mb="4" isLoading={connectURLStatus === 'loading'} onClick={onEnable}>
            {t('integrations:enable')}
          </Button>
        </Flex>
      </Flex>
    </div>
  );
};

export default ConnectorSetup;
